/**
 * Tiny observable registry used for every extension point of the web app
 * (bottom panes, sidebar tabs, top-bar actions, header slots, commands).
 *
 * Registering an entry with an existing id REPLACES it – that is how later features swap
 * the placeholder registrations for their real implementations.
 */
export interface RegistryEntry {
  id: string;
  order?: number;
}

export interface Registry<T extends RegistryEntry> {
  /** Adds or replaces an entry. Returns a function that removes exactly this registration. */
  register(entry: T): () => void;
  unregister(id: string): void;
  get(id: string): T | undefined;
  /** Entries sorted by `order` (ascending, default 0), then by registration sequence. */
  list(): readonly T[];
  subscribe(listener: () => void): () => void;
  /** Monotonic counter, changes on every mutation (usable as useSyncExternalStore snapshot). */
  version(): number;
}

export function createRegistry<T extends RegistryEntry>(): Registry<T> {
  const entries = new Map<string, { entry: T; seq: number }>();
  const listeners = new Set<() => void>();
  let seq = 0;
  let ver = 0;
  let cache: readonly T[] | null = null;

  const emit = (): void => {
    ver += 1;
    cache = null;
    for (const l of [...listeners]) l();
  };

  return {
    register(entry) {
      const prev = entries.get(entry.id);
      const slot = { entry, seq: prev ? prev.seq : seq++ };
      entries.set(entry.id, slot);
      emit();
      return () => {
        if (entries.get(entry.id) === slot) {
          entries.delete(entry.id);
          emit();
        }
      };
    },
    unregister(id) {
      if (entries.delete(id)) emit();
    },
    get(id) {
      return entries.get(id)?.entry;
    },
    list() {
      if (cache === null) {
        cache = [...entries.values()]
          .sort((a, b) => (a.entry.order ?? 0) - (b.entry.order ?? 0) || a.seq - b.seq)
          .map((s) => s.entry);
      }
      return cache;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    version() {
      return ver;
    },
  };
}
